'use client'

import { useMutation } from '@tanstack/react-query'

import { useApplyReissuedTokens } from '@/features/auth/hooks/use-apply-reissued-tokens'
import { changePassword, confirmPasswordReset, requestPasswordReset } from '@/service/password.api'
import { ChangePasswordResponse } from '@/types/generated/common/auth'
import {
  ConfirmPasswordResetResponse,
  RequestPasswordResetResponse,
} from '@/types/generated/public_api'
import type { ErrorResponse } from '@/types/service'

export const useRequestPasswordReset = ({
  onSuccess,
  onError,
}: {
  onSuccess: (response: RequestPasswordResetResponse) => void
  onError: (error: ErrorResponse) => void
}) => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: requestPasswordReset,
    onSuccess,
    onError,
  })

  return {
    mutateAsync,
    isPending,
  }
}

export const useConfirmPasswordReset = ({
  onSuccess,
  onError,
}: {
  onSuccess: (response: ConfirmPasswordResetResponse) => void
  onError: (error: ErrorResponse) => void
}) => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: confirmPasswordReset,
    onSuccess,
    onError,
  })

  return {
    mutateAsync,
    isPending,
  }
}

export const useChangePassword = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (response: ChangePasswordResponse) => void
  onError?: (error: ErrorResponse) => void
} = {}) => {
  const applyReissuedTokens = useApplyReissuedTokens()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: changePassword,
    onSuccess: async (response) => {
      // Nuke-and-reissue: every other session died and ours got fresh tokens —
      // adopt them or the tab that just changed the password logs itself out.
      await applyReissuedTokens(response)
      onSuccess?.(response)
    },
    onError,
  })

  return {
    mutateAsync,
    isPending,
  }
}
